import React, { useState, useEffect } from "react";
import { adminFetchLeads, adminUpdateLead, adminDeleteLead } from "../../api";
import { Trash2, Mail, Phone, Car, ChevronDown } from "lucide-react";
import { useToast } from "../../hooks/use-toast";

const typeLabels = { enquiry: "Car Enquiry", contact: "Contact", wanted: "Car Wanted" };
const statusColors = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-yellow-100 text-yellow-800",
  closed: "bg-gray-200 text-gray-700",
};

const LeadManager = () => {
  const [leads, setLeads] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const { toast } = useToast();

  const load = () => {
    const params = {};
    if (typeFilter) params.type = typeFilter;
    if (statusFilter) params.status = statusFilter;
    adminFetchLeads(params).then(setLeads).catch(() => setLeads([]));
  };
  useEffect(() => { load(); }, [typeFilter, statusFilter]);

  const changeStatus = async (lead, status) => {
    await adminUpdateLead(lead.id, { status });
    toast({ title: "Status updated" });
    load();
  };

  const saveNotes = async (lead, notes) => {
    await adminUpdateLead(lead.id, { notes });
    toast({ title: "Notes saved" });
    load();
  };

  const remove = async (lead) => {
    if (!window.confirm("Delete this lead?")) return;
    await adminDeleteLead(lead.id);
    toast({ title: "Lead deleted" });
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h2 className="font-serif-display text-2xl font-bold gpm-text-green">CRM — Leads ({leads.length})</h2>
        <div className="flex gap-3">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 border border-black/15 outline-none text-sm bg-white">
            <option value="">All types</option>
            <option value="enquiry">Car Enquiry</option>
            <option value="contact">Contact</option>
            <option value="wanted">Car Wanted</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border border-black/15 outline-none text-sm bg-white">
            <option value="">All statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {leads.length === 0 ? (
        <div className="bg-white border border-black/10 py-20 text-center text-gray-500">No leads yet.</div>
      ) : (
        <div className="space-y-3">
          {leads.map((l) => (
            <div key={l.id} className="bg-white border border-black/10">
              <div className="p-4 flex flex-wrap items-center gap-3">
                <span className="text-xs font-semibold uppercase px-2 py-1 rounded gpm-bg-green gpm-text-cream">{typeLabels[l.type] || l.type}</span>
                <div className="flex-1 min-w-[180px]">
                  <div className="font-semibold gpm-text-green">{l.name}</div>
                  <div className="text-xs text-gray-500 flex flex-wrap gap-3">
                    <span className="flex items-center gap-1"><Mail size={12} /> {l.email}</span>
                    {l.phone && <span className="flex items-center gap-1"><Phone size={12} /> {l.phone}</span>}
                    {l.carTitle && <span className="flex items-center gap-1"><Car size={12} /> {l.carTitle}</span>}
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[l.status]}`}>{l.status}</span>
                <select value={l.status} onChange={(e) => changeStatus(l, e.target.value)} className="px-2 py-1.5 border border-black/15 outline-none text-xs bg-white">
                  <option value="new">New</option><option value="contacted">Contacted</option><option value="closed">Closed</option>
                </select>
                <button onClick={() => setExpanded(expanded === l.id ? null : l.id)} className="p-2 text-gray-500"><ChevronDown size={18} className={expanded === l.id ? "rotate-180 transition-transform" : "transition-transform"} /></button>
                <button onClick={() => remove(l)} className="p-2 text-gray-500 hover:text-red-600"><Trash2 size={16} /></button>
              </div>
              {expanded === l.id && (
                <div className="px-4 pb-4 border-t border-black/10 pt-4 space-y-3">
                  {l.make && <p className="text-sm"><b>Looking for:</b> {l.make} {l.budget && `· Budget: £${l.budget}`}</p>}
                  {l.subject && <p className="text-sm"><b>Subject:</b> {l.subject}</p>}
                  {l.message && <p className="text-sm text-gray-700 whitespace-pre-wrap"><b>Message:</b> {l.message}</p>}
                  <p className="text-xs text-gray-400">Received: {new Date(l.createdAt).toLocaleString()}</p>
                  <div>
                    <label className="text-xs font-semibold text-gray-500">Internal notes</label>
                    <textarea defaultValue={l.notes} onBlur={(e) => e.target.value !== l.notes && saveNotes(l, e.target.value)} rows={2} placeholder="Add notes (saved on blur)…" className="w-full mt-1 px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)] text-sm" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadManager;
